import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useAuthStore } from './store/authStore';

// Layouts
import AuthLayout from '@shared/components/layout/AuthLayout';
import DashboardLayout from '@shared/components/layout/DashboardLayout';
import AuthGuard from '@shared/components/guards/AuthGuard';
import GuestGuard from '@shared/components/guards/GuestGuard';
import ErrorBoundary from '@shared/components/ErrorBoundary';

// Marketing (eager - landing page)
import LandingPage from '@features/marketing/pages/LandingPage';

// Legal pages (lazy - low-traffic, independent bundle)
const PrivacyPolicy = lazy(() => import('@features/legal/pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('@features/legal/pages/TermsOfService'));
const CookiePolicy = lazy(() => import('@features/legal/pages/CookiePolicy'));
const NhisCompliance = lazy(() => import('@features/legal/pages/NhisCompliance'));

// SEO content pages (lazy — hit by search engines and converting visitors)
const HowItWorksPage = lazy(() => import('@features/marketing/pages/HowItWorksPage'));
const NhisPage = lazy(() => import('@features/marketing/pages/NhisPage'));
const FaqPage = lazy(() => import('@features/marketing/pages/FaqPage'));
const ForDoctorsPage = lazy(() => import('@features/marketing/pages/ForDoctorsPage'));
const ForPharmaciesPage = lazy(() => import('@features/marketing/pages/ForPharmaciesPage'));
const ForPatientsPage = lazy(() => import('@features/marketing/pages/ForPatientsPage'));

// Auth pages (eager - small)
import LoginPage from '@features/auth/pages/LoginPage';
import RegisterPage from '@features/auth/pages/RegisterPage';
import ForgotPasswordPage from '@features/auth/pages/ForgotPasswordPage';
const ResetPasswordPage = lazy(() => import('@features/auth/pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('@features/auth/pages/VerifyEmailPage'));

// Patient pages (lazy)
const PatientDashboard = lazy(() => import('@features/patient/pages/PatientDashboard'));
const MyAppointments = lazy(() => import('@features/patient/pages/MyAppointments'));
const BookAppointment = lazy(() => import('@features/patient/pages/BookAppointment'));
const VitalsTracker = lazy(() => import('@features/patient/pages/VitalsTracker'));
const MedicationManager = lazy(() => import('@features/patient/pages/MedicationManager'));
const HealthRecords = lazy(() => import('@features/patient/pages/HealthRecords'));
const LabResults = lazy(() => import('@features/patient/pages/LabResults'));
const PatientPrescriptions = lazy(() => import('@features/patient/pages/PatientPrescriptions'));
const PharmacyOrder = lazy(() => import('@features/patient/pages/PharmacyOrder'));
const NurseServices = lazy(() => import('@features/patient/pages/NurseServices'));
const PatientMessages = lazy(() => import('@features/patient/pages/PatientMessages'));
const PatientAI = lazy(() => import('@features/patient/pages/PatientAI'));
const PatientConsultation = lazy(() => import('@features/patient/pages/PatientConsultation'));
const PatientBilling = lazy(() => import('@features/patient/pages/PatientBilling'));

// Clinician pages (lazy)
const ClinicianDashboard = lazy(() => import('@features/clinician/pages/ClinicianDashboard'));
const PatientQueue = lazy(() => import('@features/clinician/pages/PatientQueue'));
const ConsultationPage = lazy(() => import('@features/clinician/pages/ConsultationPage'));
const SoapNoteEditor = lazy(() => import('@features/clinician/pages/SoapNoteEditor'));
const ClinicianSchedule = lazy(() => import('@features/clinician/pages/ClinicianSchedule'));
const ClinicianPatients = lazy(() => import('@features/clinician/pages/ClinicianPatients'));
const ClinicianPrescriptions = lazy(() => import('@features/clinician/pages/ClinicianPrescriptions'));
const ClinicianLabOrders = lazy(() => import('@features/clinician/pages/ClinicianLabOrders'));
const ClinicianCredentials = lazy(() => import('@features/clinician/pages/ClinicianCredentials'));
const ClinicianAI = lazy(() => import('@features/clinician/pages/ClinicianAI'));
const ClinicianAnalytics = lazy(() => import('@features/clinician/pages/ClinicianAnalytics'));
const NurseBookings = lazy(() => import('@features/clinician/pages/NurseBookings'));

// Pharmacy pages (lazy)
const PharmacyDashboard = lazy(() => import('@features/pharmacy/pages/PharmacyDashboard'));
const PharmacyOrders = lazy(() => import('@features/pharmacy/pages/PharmacyOrders'));
const PharmacyInventory = lazy(() => import('@features/pharmacy/pages/PharmacyInventory'));
const PharmacyPrescriptions = lazy(() => import('@features/pharmacy/pages/PharmacyPrescriptions'));
const PharmacyNHIS = lazy(() => import('@features/pharmacy/pages/PharmacyNHIS'));
const PharmacyAnalytics = lazy(() => import('@features/pharmacy/pages/PharmacyAnalytics'));

// Laboratory pages (lazy)
const LabDashboard = lazy(() => import('@features/laboratory/pages/LabDashboard'));
const LabOrders = lazy(() => import('@features/laboratory/pages/LabOrders'));
const LabResultEntry = lazy(() => import('@features/laboratory/pages/LabResultEntry'));
const LabTestCatalog = lazy(() => import('@features/laboratory/pages/LabTestCatalog'));
const LabAnalytics = lazy(() => import('@features/laboratory/pages/LabAnalytics'));

// Facility pages (lazy)
const FacilityDashboard = lazy(() => import('@features/facility/pages/FacilityDashboard'));
const StaffManagement = lazy(() => import('@features/facility/pages/StaffManagement'));
const FacilitySettings = lazy(() => import('@features/facility/pages/FacilitySettings'));
const FacilityAnalytics = lazy(() => import('@features/facility/pages/FacilityAnalytics'));

// Admin pages (lazy)
const AdminDashboard = lazy(() => import('@features/admin/pages/AdminDashboard'));
const UserManagement = lazy(() => import('@features/admin/pages/UserManagement'));
const FacilityVerification = lazy(() => import('@features/admin/pages/FacilityVerification'));
const KYCReview = lazy(() => import('@features/admin/pages/KYCReview'));
const NHISManagement = lazy(() => import('@features/admin/pages/NHISManagement'));
const PlatformAnalytics = lazy(() => import('@features/admin/pages/PlatformAnalytics'));
const SystemHealth = lazy(() => import('@features/admin/pages/SystemHealth'));
const Integrations = lazy(() => import('@features/admin/pages/Integrations'));

// Shop (storefront — public browse, login-gated checkout)
const ShopLayout = lazy(() => import('@features/shop/components/ShopLayout'));
const ShopHome = lazy(() => import('@features/shop/pages/ShopHome'));
const ShopCatalog = lazy(() => import('@features/shop/pages/ShopCatalog'));
const ProductDetail = lazy(() => import('@features/shop/pages/ProductDetail'));
const CartPage = lazy(() => import('@features/shop/pages/CartPage'));
const CheckoutPage = lazy(() => import('@features/shop/pages/CheckoutPage'));
const ShopOrders = lazy(() => import('@features/shop/pages/ShopOrders'));
const OrderConfirmation = lazy(() => import('@features/shop/pages/OrderConfirmation'));
const ShopManagement = lazy(() => import('@features/admin/pages/ShopManagement'));

// Frontend batch — new feature pages
const Documents = lazy(() => import('@features/patient/pages/Documents'));
const Dependants = lazy(() => import('@features/patient/pages/Dependants'));
const PrivacyCenter = lazy(() => import('@features/patient/pages/PrivacyCenter'));
const Favourites = lazy(() => import('@features/patient/pages/Favourites'));
const LabTrends = lazy(() => import('@features/patient/pages/LabTrends'));
const Refunds = lazy(() => import('@features/patient/pages/Refunds'));
const ClinicianEarnings = lazy(() => import('@features/clinician/pages/Earnings'));
const PatientClinicalRecord = lazy(() => import('@features/clinician/pages/PatientClinicalRecord'));
const NhisTariffs = lazy(() => import('@features/admin/pages/NhisTariffs'));
const ProviderPayouts = lazy(() => import('@features/admin/pages/ProviderPayouts'));
const RefundsAdmin = lazy(() => import('@features/admin/pages/RefundsAdmin'));
const Governance = lazy(() => import('@features/admin/pages/Governance'));

// Shared pages
const NotificationsPage = lazy(() => import('@features/shared/pages/NotificationsPage'));
const SettingsPage = lazy(() => import('@features/shared/pages/SettingsPage'));
const NotFoundPage = lazy(() => import('@features/shared/pages/NotFoundPage'));

function PageLoader() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: 300, gap: 12, color: 'var(--heka-gray-400)', fontSize: 14,
    }}>
      <div style={{
        width: 24, height: 24, border: '3px solid var(--heka-gray-200)',
        borderTopColor: 'var(--heka-primary-500)', borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
      Loading...
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function App() {
  const { hydrate } = useAuthStore();

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* ─── Public ─── */}
        <Route path="/" element={<LandingPage />} />

        {/* ─── Legal ─── */}
        <Route path="/legal/privacy" element={<PrivacyPolicy />} />
        <Route path="/legal/terms" element={<TermsOfService />} />
        <Route path="/legal/cookies" element={<CookiePolicy />} />
        <Route path="/legal/nhis-compliance" element={<NhisCompliance />} />

        {/* ─── SEO content pages (public) ─── */}
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/nhis" element={<NhisPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/for-patients" element={<ForPatientsPage />} />
        <Route path="/for-doctors" element={<ForDoctorsPage />} />
        <Route path="/for-pharmacies" element={<ForPharmaciesPage />} />

        {/* ─── Shop storefront (public browse; checkout & orders gated per-page) ─── */}
        <Route path="/shop" element={<ShopLayout />}>
          <Route index element={<ShopHome />} />
          <Route path="catalog" element={<ShopCatalog />} />
          <Route path="product/:slug" element={<ProductDetail />} />
          <Route path="cart" element={<CartPage />} />
          <Route path="checkout" element={<CheckoutPage />} />
          <Route path="orders" element={<ShopOrders />} />
          <Route path="orders/:id" element={<OrderConfirmation />} />
        </Route>

        {/* ─── Auth (guest only) ─── */}
        <Route element={<GuestGuard><AuthLayout /></GuestGuard>}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
        </Route>

        {/* ─── Patient Routes ─── */}
        <Route element={<AuthGuard allowedRoles={['patient']}><DashboardLayout /></AuthGuard>}>
          <Route path="/patient/dashboard" element={<PatientDashboard />} />
          <Route path="/patient/appointments" element={<MyAppointments />} />
          <Route path="/patient/book" element={<BookAppointment />} />
          <Route path="/patient/vitals" element={<VitalsTracker />} />
          <Route path="/patient/medications" element={<MedicationManager />} />
          <Route path="/patient/records" element={<HealthRecords />} />
          <Route path="/patient/lab-results" element={<LabResults />} />
          <Route path="/patient/prescriptions" element={<PatientPrescriptions />} />
          <Route path="/patient/pharmacy" element={<PharmacyOrder />} />
          <Route path="/patient/billing" element={<PatientBilling />} />
          <Route path="/patient/nurse-services" element={<NurseServices />} />
          <Route path="/patient/messages" element={<PatientMessages />} />
          <Route path="/patient/ai" element={<PatientAI />} />
          <Route path="/patient/consultation/:id" element={<PatientConsultation />} />
          <Route path="/patient/documents" element={<Documents />} />
          <Route path="/patient/dependants" element={<Dependants />} />
          <Route path="/patient/privacy" element={<PrivacyCenter />} />
          <Route path="/patient/favourites" element={<Favourites />} />
          <Route path="/patient/lab-trends" element={<LabTrends />} />
          <Route path="/patient/refunds" element={<Refunds />} />
        </Route>

        {/* ─── Clinician Routes (doctor, nurse, PA) ─── */}
        <Route element={<AuthGuard allowedRoles={['doctor', 'nurse', 'physician_assistant']}><DashboardLayout /></AuthGuard>}>
          <Route path="/clinician/dashboard" element={<ClinicianDashboard />} />
          <Route path="/clinician/queue" element={<PatientQueue />} />
          <Route path="/clinician/consultations" element={<ConsultationPage />} />
          <Route path="/clinician/consultations/:id" element={<ConsultationPage />} />
          <Route path="/clinician/soap-notes" element={<SoapNoteEditor />} />
          <Route path="/clinician/soap-notes/:consultationId" element={<SoapNoteEditor />} />
          <Route path="/clinician/schedule" element={<ClinicianSchedule />} />
          <Route path="/clinician/patients" element={<ClinicianPatients />} />
          <Route path="/clinician/prescriptions" element={<ClinicianPrescriptions />} />
          <Route path="/clinician/lab-orders" element={<ClinicianLabOrders />} />
          <Route path="/clinician/credentials" element={<ClinicianCredentials />} />
          <Route path="/clinician/ai" element={<ClinicianAI />} />
          <Route path="/clinician/analytics" element={<ClinicianAnalytics />} />
          <Route path="/clinician/earnings" element={<ClinicianEarnings />} />
          <Route path="/clinician/patients/:patientId/clinical" element={<PatientClinicalRecord />} />
          <Route path="/clinician/messages" element={<PatientMessages />} />
          <Route path="/clinician/bookings" element={<NurseBookings />} />
        </Route>

        {/* ─── Pharmacy Routes ─── */}
        <Route element={<AuthGuard allowedRoles={['pharmacist']}><DashboardLayout /></AuthGuard>}>
          <Route path="/pharmacy/dashboard" element={<PharmacyDashboard />} />
          <Route path="/pharmacy/orders" element={<PharmacyOrders />} />
          <Route path="/pharmacy/inventory" element={<PharmacyInventory />} />
          <Route path="/pharmacy/prescriptions" element={<PharmacyPrescriptions />} />
          <Route path="/pharmacy/nhis" element={<PharmacyNHIS />} />
          <Route path="/pharmacy/analytics" element={<PharmacyAnalytics />} />
          <Route path="/pharmacy/messages" element={<PatientMessages />} />
        </Route>

        {/* ─── Laboratory Routes ─── */}
        <Route element={<AuthGuard allowedRoles={['lab_tech']}><DashboardLayout /></AuthGuard>}>
          <Route path="/laboratory/dashboard" element={<LabDashboard />} />
          <Route path="/laboratory/orders" element={<LabOrders />} />
          <Route path="/laboratory/results" element={<LabResultEntry />} />
          <Route path="/laboratory/catalog" element={<LabTestCatalog />} />
          <Route path="/laboratory/analytics" element={<LabAnalytics />} />
          <Route path="/laboratory/messages" element={<PatientMessages />} />
        </Route>

        {/* ─── Facility Routes ─── */}
        <Route element={<AuthGuard allowedRoles={['admin']}><DashboardLayout /></AuthGuard>}>
          <Route path="/facility/dashboard" element={<FacilityDashboard />} />
          <Route path="/facility/staff" element={<StaffManagement />} />
          <Route path="/facility/settings" element={<FacilitySettings />} />
          <Route path="/facility/analytics" element={<FacilityAnalytics />} />
        </Route>

        {/* ─── Admin Routes ─── */}
        <Route element={<AuthGuard allowedRoles={['admin', 'super_admin']}><DashboardLayout /></AuthGuard>}>
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/users" element={<UserManagement />} />
          <Route path="/admin/facilities" element={<FacilityVerification />} />
          <Route path="/admin/kyc" element={<KYCReview />} />
          <Route path="/admin/nhis" element={<NHISManagement />} />
          <Route path="/admin/shop" element={<ShopManagement />} />
          <Route path="/admin/analytics" element={<PlatformAnalytics />} />
          <Route path="/admin/health" element={<SystemHealth />} />
          <Route path="/admin/integrations" element={<Integrations />} />
          <Route path="/admin/nhis-tariffs" element={<NhisTariffs />} />
          <Route path="/admin/payouts" element={<ProviderPayouts />} />
          <Route path="/admin/refunds" element={<RefundsAdmin />} />
          <Route path="/admin/governance" element={<Governance />} />
        </Route>

        {/* ─── Shared authenticated routes ─── */}
        <Route element={<AuthGuard><DashboardLayout /></AuthGuard>}>
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Route>

        {/* ─── Fallback: real 404 (was silently rendering the marketing LandingPage) ─── */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
